"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const array_flatten_1 = __importDefault(require("array-flatten"));
const common_1 = require("../common");
const CreaseComponent_1 = require("./CreaseComponent");
const CreaseEdge_1 = require("./CreaseEdge");
const CreaseEdgeLoop_1 = require("./CreaseEdgeLoop");
const CreaseEdgeRef_1 = require("./CreaseEdgeRef");
const CreaseFace_1 = require("./CreaseFace");
const CreaseVertex_1 = require("./CreaseVertex");
/**
 * A representation of a 2D dieline.
 */
class Crease {
    /**
     * Constructs a Crease object from its vertices, edges, and faces.
     * @param {CreaseVertex[]} vertices The vertices to use.
     * @param {CreaseEdge[]} edges The edges to use.
     * @param {CreaseFace[]} faces The faces to use.
     */
    constructor(vertices, edges, faces) {
        // Check inputs.
        common_1.check.isArray('vertices', vertices, undefined, (name, vertex) => common_1.check.isInstanceOf(name, vertex, CreaseVertex_1.CreaseVertex));
        common_1.check.isArray('edges', edges, undefined, (name, edge) => common_1.check.isInstanceOf(name, edge, CreaseEdge_1.CreaseEdge));
        common_1.check.isArray('faces', faces, undefined, (name, face) => common_1.check.isInstanceOf(name, face, CreaseFace_1.CreaseFace));
        // Store the vertices, edges, and faces.
        this._vertices = vertices;
        this._edges = edges;
        this._faces = faces;
        this._components = [];
        // Add any missing connectivity information then validate everything.
        this._addConnectivity();
        this._createComponents();
        this._validate();
    }
    /**
     * Returns an array of all vertices.
     * @returns {CreaseVertex[]} All vertices.
     */
    get vertices() {
        return this._vertices;
    }
    /**
     * Returns an array of all edges.
     * @returns {CreaseEdge[]} All edges.
     */
    get edges() {
        return this._edges;
    }
    /**
     * Returns an array of all faces.
     * @returns {CreaseFace[]} All faces.
     */
    get faces() {
        return this._faces;
    }
    /**
     * Returns an array of all components.
     * @returns {CreaseComponent[]} All components.
     */
    get components() {
        return this._components;
    }
    /**
     * Returns an object that can be serialized using `JSON.stringify`.
     * @returns {object} The serializable representation of this Crease object.
     */
    toJSON() {
        return {
            version: 1,
            vertices: this.vertices.map(vertex => vertex.toJSON()),
            edges: this.edges.map(edge => edge.toJSON()),
            faces: this.faces.map(face => face.toJSON()),
        };
    }
    /**
     * Constructs a new Crease object from its JSON serializable representation.
     * @param {CreaseJSON} json The JSON serializable representation of a Crease object.
     * @returns {Crease} A new Crease object.
     */
    static fromJSON(json) {
        // Get the version of the file format. Use 0.1 for the earliest Crease JSON files, which
        // had no version.
        const version = json.version || 0.1;
        if (version < 1) {
            throw new Error('Expected json to have version 1 or greater.');
        }
        // Check input.
        common_1.check.hasProperty('json', json, 'vertices', (name, vertices) => {
            common_1.check.isArray(name, vertices, undefined, (vName, v) => {
                common_1.check.hasProperty(vName, v, 'position', common_1.check.is2DPoint);
            });
        });
        common_1.check.hasProperty('json', json, 'edges', (name, edges) => {
            common_1.check.isArray(name, edges, undefined, (edgeName, edge) => {
                common_1.check.hasProperty(edgeName, edge, 'vertex1', (v1Name, v1) => common_1.check.isInRange(v1Name, v1, 0, json.vertices.length - 1));
                common_1.check.hasProperty(edgeName, edge, 'vertex2', (v2Name, v2) => common_1.check.isInRange(v2Name, v2, 0, json.vertices.length - 1));
                if (edge.assignment) {
                    common_1.check.isAmong(`${edgeName}.assignment`, edge.assignment, ['U', 'C', 'B', 'M', 'V', 'F']);
                }
                if (edge.controlPoints) {
                    common_1.check.isArray(`${edgeName}.controlPoints`, edge.controlPoints, [0, 2], common_1.check.is2DPoint);
                }
                if (edge.foldAngle) {
                    common_1.check.isInRange(`${edgeName}.foldAngle`, edge.foldAngle, 0, 180);
                }
            });
        });
        common_1.check.hasProperty('json', json, 'faces', (name, faces) => {
            common_1.check.isArray(name, faces, undefined, (fName, f) => {
                common_1.check.hasProperty(fName, f, 'edgeLoops', (elsName, els) => {
                    common_1.check.isArray(elsName, els, undefined, (elName, el) => {
                        common_1.check.hasProperty(elName, el, 'edgeRefs', (ersName, ers) => {
                            common_1.check.isArray(ersName, ers, undefined, (erName, er) => {
                                common_1.check.hasProperty(erName, er, 'edge', (eName, e) => common_1.check.isInRange(eName, e, 0, json.edges.length - 1));
                                if (er.isReversed !== undefined) {
                                    common_1.check.isBoolean(`${erName}.isReversed`, er.isReversed);
                                }
                            });
                        });
                    });
                });
            });
        });
        // Create vertices, edges, and faces.
        const vertices = json.vertices.map((vertex, i) => new CreaseVertex_1.CreaseVertex(i, vertex.position));
        const edges = json.edges.map((edge, i) => new CreaseEdge_1.CreaseEdge(i, vertices[edge.vertex1], vertices[edge.vertex2], edge.assignment, edge.controlPoints, edge.foldAngle));
        const faces = json.faces.map((face, i) => {
            const edgeLoops = face.edgeLoops.map((loop) => {
                const loopEdgeRefs = loop.edgeRefs;
                const edgeRefs = loopEdgeRefs.map((edgeRef) => new CreaseEdgeRef_1.CreaseEdgeRef(edges[edgeRef.edge], edgeRef.isReversed));
                return new CreaseEdgeLoop_1.CreaseEdgeLoop(edgeRefs);
            });
            return new CreaseFace_1.CreaseFace(i, edgeLoops, face.category);
        });
        // Create a Crease object.
        return new Crease(vertices, edges, faces);
    }
    /**
     * Constructs a new Crease object from a .crease file path.
     * @param {string} path The path to a .crease file.
     * @param {ReadableFileSystem} fs The Node.js file system.
     * @returns {Crease} A new Crease object.
     */
    static fromFile(path, fs) {
        const jsonString = fs.readFileSync(path, 'utf8');
        const json = JSON.parse(jsonString);
        return Crease.fromJSON(json);
    }
    /**
     * Returns an object that can be serialized in Fold-compatible format using `JSON.stringify`.
     * @returns {FoldJSON} The Fold-compatible representation of this Crease object.
     */
    toFoldJSON() {
        // TODO: Add an option to omit holes, making the output truly Fold-compatible.
        const verticesCoords = this.vertices.map(vertex => vertex.position.slice());
        const edgesVertices = this.edges.map(edge => [edge.vertex1.index, edge.vertex2.index]);
        const edgesAssignment = this.edges.map(edge => edge.assignment);
        const edgesControlPoints = this.edges.map(edge => edge.controlPoints.slice());
        const edgesFoldAngle = this.edges.map(edge => (edge.foldAngle !== null
            ? common_1.utils.convertToRadians(edge.foldAngle) : edge.foldAngle));
        const facesVertices = this.faces.map(face => face.edgeLoops.map(loop => loop.edgeRefs.map(edge => edge.vertex1.index)));
        return {
            vertices_coords: verticesCoords,
            edges_vertices: edgesVertices,
            edges_assignment: edgesAssignment,
            edges_controlPoints: edgesControlPoints,
            edges_foldAngle: edgesFoldAngle,
            faces_vertices: facesVertices,
        };
    }
    /**
     * Constructs a new Crease object from its Fold-compatible representation.
     * @param {FoldJSON} foldJSON The Fold-compatible representation of a Crease object.
     * @returns {Crease} A new Crease object.
     */
    static fromFoldJSON(foldJSON) {
        // TODO: Support truly Fold-compatible objects, where faces don't permit holes.
        // This will require adding extra edges.
        // Check input.
        common_1.check.hasProperty('foldJSON', foldJSON, 'vertices_coords', (name, verticesCoords) => {
            common_1.check.isArray(name, verticesCoords, undefined, common_1.check.is2DPoint);
        });
        common_1.check.hasProperty('foldJSON', foldJSON, 'edges_vertices', (name, edgesVertices) => {
            common_1.check.isArrayOfArrays(name, edgesVertices, undefined, 2, (vName, v) => {
                common_1.check.isInRange(vName, v, 0, foldJSON.vertices_coords.length);
            });
        });
        common_1.check.hasProperty('foldJSON', foldJSON, 'edges_assignment', (name, edgesAssignment) => {
            common_1.check.isArray(name, edgesAssignment, foldJSON.edges_vertices.length, (aName, a) => common_1.check.isAmong(aName, a, ['U', 'C', 'B', 'M', 'V', null]));
        });
        if (foldJSON.edges_controlPoints) {
            common_1.check.isArrayOfArrays('foldJSON.edges_controlPoints', foldJSON.edges_controlPoints, foldJSON.edges_vertices.length, [0, 2], common_1.check.is2DPoint);
        }
        if (foldJSON.edges_foldAngle) {
            common_1.check.isArray('foldJSON.edges_foldAngle', foldJSON.edges_foldAngle, foldJSON.edges_vertices.length, (name, value) => {
                if (value !== null) {
                    common_1.check.isInRange(name, value, 0, Math.PI);
                }
            });
        }
        if (foldJSON.faces_vertices) {
            common_1.check.isArrayOfArrays('foldJSON.faces_vertices', foldJSON.faces_vertices, undefined, undefined, (name, value) => common_1.check.isArray(name, value, undefined, (vName, v) => {
                common_1.check.isInRange(vName, v, 0, foldJSON.vertices_coords.length);
            }));
        }
        // Create vertices, edges, and faces.
        const vertices = foldJSON.vertices_coords.map((position, i) => new CreaseVertex_1.CreaseVertex(i, position));
        const edges = foldJSON.edges_vertices.map((e, i) => {
            const vertex1 = vertices[e[0]];
            const vertex2 = vertices[e[1]];
            const assignment = foldJSON.edges_assignment ? foldJSON.edges_assignment[i] : null;
            const controlPoints = foldJSON.edges_controlPoints ? foldJSON.edges_controlPoints[i]
                : [];
            const angle = foldJSON.edges_foldAngle && common_1.utils.isNumber(foldJSON.edges_foldAngle[i])
                ? common_1.utils.convertToDegrees(foldJSON.edges_foldAngle[i]) : null;
            return new CreaseEdge_1.CreaseEdge(i, vertex1, vertex2, assignment, controlPoints, angle);
        });
        const facesVertices = foldJSON.faces_vertices || [];
        const faces = facesVertices.map((loops, i) => {
            const edgeLoops = loops.map((loop) => {
                const n = loop.length;
                const edgeRefs = loop.map((v1, j) => {
                    const v2 = loop[(j + 1) % n];
                    let edge = edges.find((e) => e.vertex1.index === v1 && e.vertex2.index === v2);
                    let isReversed = false;
                    if (!edge) {
                        edge = edges.find(e => e.vertex1.index === v2 && e.vertex2.index === v1);
                        isReversed = true;
                    }
                    if (!edge) {
                        throw new Error('Could not find matching edge.');
                    }
                    return new CreaseEdgeRef_1.CreaseEdgeRef(edge, isReversed);
                });
                return new CreaseEdgeLoop_1.CreaseEdgeLoop(edgeRefs);
            });
            return new CreaseFace_1.CreaseFace(i, edgeLoops);
        });
        // Create a Crease object.
        return new Crease(vertices, edges, faces);
    }
    /**
     * Creates a copy of this Crease object.
     * @returns {Crease} A copy of this Crease object.
     */
    copy() {
        // Make a deep copy by serializing and deserializing.
        const json = JSON.parse(JSON.stringify(this.toJSON()));
        return Crease.fromJSON(json);
    }
    /**
     * Splits this Crease object's connected components into multiple Crease objects.
     * @param crease A Crease object.
     * @returns An array of Crease objects, the first of which is always this Crease object.
     */
    splitComponents() {
        // If there's only one component, we don't need to do any work.
        if (this.components.length <= 1) {
            return [this];
        }
        // Create a new Crease object for each connected component after the first.
        const creases = [];
        this.components.forEach((component, i) => {
            const { faces } = component;
            const edges = Array.from(new Set(array_flatten_1.default(component.faces.map(face => face.edgeLoops.map(loop => loop.edgeRefs.map(edgeRef => edgeRef.edge))))));
            const vertices = Array.from(new Set(array_flatten_1.default(edges.map(edge => [edge.vertex1, edge.vertex2]))));
            let newCrease = this;
            if (i === 0) {
                // Mutate the current Crease object to contain just the first component.
                this._vertices = vertices;
                this._edges = edges;
                this._faces = faces;
            }
            else {
                newCrease = new Crease(vertices, edges, faces);
            }
            creases.push(newCrease);
        });
        // Reindex the vertices, edges, and faces of each Crease.
        creases.forEach(crease => {
            crease.vertices.forEach((vertex, i) => {
                vertex._setIndex(i);
            });
            crease.edges.forEach((edge, i) => {
                edge._setIndex(i);
            });
            crease.faces.forEach((face, i) => {
                face._setIndex(i);
            });
        });
        // Complete the reinitialization of this Crease object.
        this._components = [];
        this._addConnectivity();
        this._createComponents();
        this._validate();
        // Make sure that vertices, edges, and faces are not re-used in multiple Crease objects.
        creases.forEach((crease, i) => {
            const otherCreases = creases.filter((c, j) => j > i);
            crease.vertices.forEach(v1 => {
                const j = otherCreases.findIndex(c => c.vertices.some(v2 => v1 === v2));
                if (j >= 0) {
                    throw new Error(`Vertex ${v1.index} appears in Crease ${i} and ${j}.`);
                }
            });
            crease.edges.forEach(e1 => {
                const j = otherCreases.findIndex(c => c.edges.some(e2 => e1 === e2));
                if (j >= 0) {
                    throw new Error(`Edge ${e1.index} appears in Crease ${i} and ${j}.`);
                }
            });
            crease.faces.forEach(f1 => {
                const j = otherCreases.findIndex(c => c.faces.some(f2 => f1 === f2));
                if (j >= 0) {
                    throw new Error(`Face ${f1.index} appears in Crease ${i} and ${j}.`);
                }
            });
        });
        // Return the array of Crease objects.
        return creases;
    }
    /**
     * Gets the bounds of all points in this Crease object.
     * @returns {Bounds2} The bounds of this Crease object.
     */
    getBounds() {
        const vertexPoints = this.vertices.map(vertex => vertex.position);
        const allPoints = this.edges.reduce((points, edge) => points.concat(edge.controlPoints), vertexPoints);
        return common_1.geom.getBounds(allPoints);
    }
    /**
     * Moves all points by the specified offset.
     * @param {Vector2} offset A 2D offset vector.
     */
    translate(offset) {
        common_1.check.is2DPoint('offset', offset);
        // Update vertex positions.
        this.vertices.forEach((vertex) => {
            vertex._setPosition(common_1.vec.add(vertex.position, offset));
        });
        // Update edge control points.
        this.edges.forEach((edge) => {
            edge._setControlPoints(edge.controlPoints.map(p => common_1.vec.add(p, offset)));
        });
    }
    /**
     * Scales all points by the specified factor.
     * @param {number} factor A scaling factor.
     */
    scale(factor) {
        common_1.check.isNumber('factor', factor);
        // Update vertex positions.
        this.vertices.forEach((vertex) => {
            vertex._setPosition(common_1.vec.multiply(vertex.position, factor));
        });
        // Update edge control points.
        this.edges.forEach((edge) => {
            edge._setControlPoints(edge.controlPoints.map(p => common_1.vec.multiply(p, factor)));
        });
    }
    /**
     * Scales all points so that the larger of width and height is one.
     */
    normalize() {
        const bounds = this.getBounds();
        const size = common_1.vec.subtract(bounds.max, bounds.min);
        const maxDim = Math.max(size[0], size[1]);
        this.scale(1 / maxDim);
    }
    /**
     * Translates all points so that the midpoint of the bounds lies at the desired center.
     * @param {Vector2} desiredCenter The desired center point.
     */
    centerAround(desiredCenter = [0, 0]) {
        const bounds = this.getBounds();
        const currentCenter = common_1.vec.divide(common_1.vec.add(bounds.min, bounds.max), 2);
        const offset = common_1.vec.subtract(desiredCenter, currentCenter);
        this.translate(offset);
    }
    /**
     * Translates all points so that the minimum position is the origin.
     */
    alignToZero() {
        const { min } = this.getBounds();
        this.translate(common_1.vec.multiply(min, -1));
    }
    /**
     * Adds connectivity information to vertices and edges.
     * @private
     */
    _addConnectivity() {
        // Add incident faces to each edge.
        this.faces.forEach((face) => {
            face.edgeLoops.forEach((loop) => {
                loop.edgeRefs.forEach((edgeRef) => {
                    const { edge, isReversed } = edgeRef;
                    edge[isReversed ? '_rightFace' : '_leftFace'] = face;
                });
            });
        });
        // Clear the incidentEdges and incidentFaces arrays in each vertex.
        this.vertices.forEach((vertex) => {
            vertex.incidentEdges.length = 0;
            vertex.incidentFaces.length = 0;
        });
        // Make sure boundary edges go in the correct direction.
        this._correctBoundaryEdgeDirection();
        // Add incident edges to each vertex.
        this.edges.forEach((edge) => {
            // Add an edge reference to each of the vertices.
            const edgeRef1 = new CreaseEdgeRef_1.CreaseEdgeRef(edge, false);
            edge.vertex1.incidentEdges.push(edgeRef1);
            const edgeRef2 = new CreaseEdgeRef_1.CreaseEdgeRef(edge, true);
            edge.vertex2.incidentEdges.push(edgeRef2);
        });
        // Sort the incident edges around each vertex.
        this.vertices.forEach((vertex) => {
            vertex.sortIncidentEdges();
        });
        // Add incident faces to each vertex.
        this.faces.forEach((face) => {
            face.edgeLoops.forEach((loop) => {
                loop.edgeRefs.forEach((edgeRef) => {
                    if (edgeRef.vertex1.incidentFaces.indexOf(face) < 0) {
                        edgeRef.vertex1.incidentFaces.push(face);
                    }
                });
            });
        });
    }
    /**
     * Corrects the direction of boundary edges, making sure the incident
     * face is always to the left of a boundary edge.
     * @private
     */
    _correctBoundaryEdgeDirection() {
        // Look for edges that have a right face but no left face.
        this.edges.forEach((edge) => {
            if (edge.rightFace && !edge.leftFace) {
                // Hold onto the valid face.
                const validFace = edge.rightFace;
                // Flip this edge.
                edge._reverse();
                // Flip references to this edge from its two vertices.
                [edge.vertex1, edge.vertex2].forEach((vertex) => {
                    vertex.incidentEdges.forEach((edgeRef) => {
                        if (edgeRef.edge === edge) {
                            edgeRef._reverse();
                        }
                    });
                });
                // Flip the reference to this edge from the left face.
                validFace.edgeLoops.forEach((loop) => {
                    loop.edgeRefs.forEach((edgeRef) => {
                        if (edgeRef.edge === edge) {
                            edgeRef._reverse();
                        }
                    });
                });
            }
        });
    }
    /**
     * Creates connected components after connectivity has been established.
     * @private
     */
    _createComponents() {
        this._components = [];
        const visited = [];
        this.faces.forEach((face) => {
            if (!visited[face.index]) {
                const connectedFaces = [face];
                visited[face.index] = true;
                Crease._collectConnectedFaces(face, visited, connectedFaces);
                this._components.push(new CreaseComponent_1.CreaseComponent(this._components.length, connectedFaces));
            }
        });
    }
    /**
     * Recursively adds all faces connected to the given face to the connectedFaces array.
     * @private
     * @param {CreaseFace} face A face.
     * @param {boolean[]} visited An array of flags indicating which faces have been visited.
     * @param {CreaseFace[]} connectedFaces An array of connected faces.
     */
    static _collectConnectedFaces(face, visited, connectedFaces) {
        face.incidentFaces.forEach((adjacentFace) => {
            if (!visited[adjacentFace.index]) {
                visited[adjacentFace.index] = true;
                connectedFaces.push(adjacentFace);
                Crease._collectConnectedFaces(adjacentFace, visited, connectedFaces);
            }
        });
    }
    /**
     * Checks invariants after connectivity has been established.
     * @private
     */
    _validate() {
        // Make sure all edges have a face on the left.
        // A faces on the right is optional.
        this.edges.forEach((edge) => {
            if (edge.rightFace && !edge.leftFace) {
                throw new Error(`Edge ${edge.index} has no left face. Right face is ${edge.rightFace ? edge.rightFace.index : 'undefined'}.`);
            }
        });
        // Make sure all faces have properly closed edge loops.
        this.faces.forEach((face) => {
            face.edgeLoops.forEach((loop, i) => {
                const n = loop.edgeRefs.length;
                loop.edgeRefs.forEach((edgeRef, j) => {
                    const k = (j + 1) % n;
                    if (edgeRef.vertex2 !== loop.edgeRefs[k].vertex1) {
                        throw new Error(`Face ${face.index}, loop ${i} (size ${n}): vertex 2 of edge ${j} does not match vertex 1 of edge ${k}.`);
                    }
                });
            });
        });
    }
    /**
     * Checks deep equality between this and another Crease object
     * @param {Crease} crease A crease.
     * @param {boolean} [logDebug = false] A flag to log unequal elements, default to false.
     * @param {number} [epsilon = 1e-2] A small epsilon to use to compare floating point values.
     * @returns {boolean} Returns true if equal, else false.
     */
    equals(crease, logDebug = false, epsilon = 1e-2) {
        let equal = true;
        const { vertices, edges, faces } = this;
        if (crease.vertices.length !== vertices.length) {
            equal = false;
            if (logDebug) {
                /* eslint-disable-next-line no-console */
                console.log(`Vertices arrays are of unequal size: ${vertices.length}, ${crease.vertices.length}`);
            }
        }
        else {
            crease.vertices.forEach((vertex, i) => {
                if (!vertex.equals(vertices[i], logDebug, epsilon)) {
                    equal = false;
                }
            });
        }
        if (crease.edges.length !== edges.length) {
            equal = false;
            if (logDebug) {
                /* eslint-disable-next-line no-console */
                console.log(`Edges arrays are of unequal size: ${edges.length}, ${crease.edges.length}`);
            }
        }
        else {
            crease.edges.forEach((edge, i) => {
                if (!edge.equals(edges[i], logDebug, epsilon)) {
                    equal = false;
                }
            });
        }
        if (crease.faces.length !== faces.length) {
            equal = false;
            if (logDebug) {
                /* eslint-disable-next-line no-console */
                console.log(`Faces arrays are of unequal size: ${faces.length}, ${crease.faces.length}`);
            }
        }
        else {
            crease.faces.forEach((face, i) => {
                if (!face.equals(faces[i], logDebug)) {
                    equal = false;
                }
            });
        }
        return equal;
    }
}
exports.Crease = Crease;
//# sourceMappingURL=Crease.js.map